import type {ShouldSkipLazyLoadFn, ShouldSkipLazyLoadType} from 'app/interface';
import {appEvents} from 'app/utils/appEvents';
import {getComponentConfiguration} from 'app/utils/config';
import type {GemSettings} from 'app/utils/config/interface';
import {generateRandomString} from 'app/utils/hash/generateRandomString';
import type {ComponentMountDataTransporter, WebComponentUnmountDataTransporter} from 'app/utils/initialize/interface';
import {lazyInitModule} from 'app/utils/initialize/lazyInitModule';
import {triggerCustomEvent} from 'app/utils/jqueryNative';
import {renderedRemotely} from 'app/utils/renderedRemotely';
import type {ComponentConfig} from 'interfaces/foundation/components/componentConfig';

export function getWebComponentLoader(gemSettings: GemSettings, lazyLoadPage?: boolean) {
    return class extends HTMLElement {
        gemSettings = gemSettings;
        reactLazyComponent!: ComponentMountDataTransporter['ReactLazyComponent'];
        shouldSkipLazyLoad: ShouldSkipLazyLoadFn | undefined;
        removePortalContent = true;
        hasConfig = true;
        #key: string | undefined;
        #disconnected = false;

        connectedCallback() {
            if (!this.gemSettings) {
                throw new Error(`Gem Settings is missing for ${this.tagName}!`);
            }

            this.#getConfig()
                .then((config) => {
                    if (this.#disconnected) {
                        return;
                    }

                    this.#key = generateRandomString();
                    let loadingType: ShouldSkipLazyLoadType = lazyLoadPage ? 'LOAD_LAZY' : 'LOAD_EAGER';
                    if (loadingType === 'LOAD_LAZY') {
                        loadingType = this.shouldSkipLazyLoad?.({config, element: this}) ?? 'LOAD_LAZY';
                    }

                    if (loadingType !== 'LOAD_LAZY') {
                        this.#initLazyComponent(config);
                    } else {
                        const moduleName = this.tagName;
                        const lazyInit = () => {
                            triggerCustomEvent(this, appEvents.componentInitializationStart, moduleName);
                            this.#initLazyComponent(config);
                        };

                        lazyInitModule({
                            lazyInit,
                            config,
                            moduleName,
                            element: this,
                            shouldSkipLazyLoad: this.shouldSkipLazyLoad,
                        });
                    }
                })
                .catch(console.error);
        }

        #initLazyComponent(config: ComponentConfig | undefined) {
            if (!this.#key) {
                throw new Error(`cId is missing for ${this.tagName}!`);
            }

            const webComponentDataTransporter: ComponentMountDataTransporter = {
                config,
                ReactLazyComponent: this.reactLazyComponent,
                element: this,
                key: this.#key,
                removePortalContent: this.removePortalContent,
            };

            const eventName = renderedRemotely(this) ? appEvents.hydrateWebComponentMount : appEvents.webComponentMount;
            triggerCustomEvent(document, eventName, webComponentDataTransporter);
        }

        /**
         * When injecting HTML as string, Web Components is inserted before the configuration script tag.
         * This method waits for the configuration to be available in the DOM.
         * @private
         */
        #getConfig() {
            return new Promise<ComponentConfig | undefined>((resolve, reject) => {
                if (!this.hasConfig) {
                    resolve(undefined);
                    return;
                }

                const config = this.#findConfig();
                if (config) {
                    resolve(config);
                    return;
                }

                if (!this.parentElement) {
                    reject(new Error('Parent element is missing'));
                    return;
                }

                const observer = new MutationObserver(() => {
                    const foundConfig = this.#findConfig();
                    if (foundConfig) {
                        observer.disconnect();
                        resolve(foundConfig);
                    }
                });

                observer.observe(this.parentElement, {childList: true, subtree: true});
            });
        }

        #findConfig() {
            try {
                return getComponentConfiguration<ComponentConfig>(this);
            } catch {
                // do nothing
            }
        }

        disconnectedCallback() {
            this.#disconnected = true;
            triggerCustomEvent(this, appEvents.componentInitializationStart, this.tagName);
            if (this.#key) {
                const webComponentUnmountDataTransporter: WebComponentUnmountDataTransporter = {
                    key: this.#key,
                };

                triggerCustomEvent(document, appEvents.webComponentUnmount, webComponentUnmountDataTransporter);
            }
        }

        getKey() {
            return this.#key;
        }
    };
}
